// src/stores/reducers/modelItem.ts
/**
 * Model Item Reducers - Enhanced with Color Support
 * 
 * Changes from original:
 * 1. Color is now included in ModelItem updates
 * 2. Added specific color update functions
 * 3. Maintains backward compatibility
 */

import { produce } from 'immer';
import { ModelItem } from 'src/types';
import { getItemByIdOrThrow } from 'src/utils';
import { State } from './types';

/**
 * Update a model item with partial updates
 * Now supports color updates
 */
export const updateModelItem = (
  id: string,
  updates: Partial<ModelItem>,
  state: State
): State => {
  const modelItem = getItemByIdOrThrow(state.model.items, id);

  const newState = produce(state, (draft) => {
    draft.model.items[modelItem.index] = { ...modelItem.value, ...updates };
  });

  return newState;
};

/**
 * Create a new model item
 * Supports optional color property
 */
export const createModelItem = (
  newModelItem: ModelItem,
  state: State
): State => {
  const newState = produce(state, (draft) => {
    draft.model.items.push(newModelItem);
  });

  return updateModelItem(newModelItem.id, newModelItem, newState);
};

/**
 * Delete a model item
 */
export const deleteModelItem = (id: string, state: State): State => {
  const modelItem = getItemByIdOrThrow(state.model.items, id);

  const newState = produce(state, (draft) => {
    draft.model.items.splice(modelItem.index, 1);
  });

  return newState;
};

// ===== NEW: COLOR-SPECIFIC REDUCERS =====

/**
 * Update only the color of a model item
 */
export const updateModelItemColor = (
  id: string,
  color: string,
  state: State
): State => {
  return updateModelItem(id, { color }, state);
};

/**
 * Batch update colors for multiple items
 */
export const updateMultipleItemColors = (
  updates: Array<{ id: string; color: string }>,
  state: State
): State => {
  return updates.reduce((currentState, { id, color }) => {
    try {
      return updateModelItemColor(id, color, currentState);
    } catch (error) {
      console.warn(`Failed to update color for item ${id}:`, error);
      return currentState;
    }
  }, state);
};

/**
 * Reset item color to undefined
 */
export const resetModelItemColor = (id: string, state: State): State => {
  const modelItem = getItemByIdOrThrow(state.model.items, id);

  const newState = produce(state, (draft) => {
    delete draft.model.items[modelItem.index].color;
  });

  return newState;
};

/**
 * Apply a color to all items with a specific icon
 */
export const colorItemsByIcon = (
  iconId: string,
  color: string,
  state: State
): State => {
  const newState = produce(state, (draft) => {
    draft.model.items.forEach((item) => {
      if (item.icon === iconId) {
        item.color = color;
      }
    });
  });

  return newState;
};